import { 
    collection, 
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    doc, 
    query, 
    orderBy, 
    where,
    Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { UpdateWindow, CreateUpdateWindowData, EditUpdateWindowData } from '../models/updateWindow';

const COLLECTION_NAME = 'updateWindows';

export class UpdateWindowService {
    // Buscar todas as janelas de atualização
    static async getAll(): Promise<UpdateWindow[]> {
        try {
            const querySnapshot = await getDocs(
                query(
                    collection(db, COLLECTION_NAME),
                    orderBy('startDate', 'desc')
                )
            );

            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.()?.toISOString(),
                updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString()
            })) as UpdateWindow[];
        } catch (error) {
            console.error('Erro ao buscar janelas de atualização:', error);
            throw new Error('Falha ao carregar janelas de atualização');
        }
    }

    // Buscar janelas de atualização ativas
    static async getActiveWindows(): Promise<UpdateWindow[]> {
        try {
            const now = new Date();
            const querySnapshot = await getDocs(
                query(
                    collection(db, COLLECTION_NAME),
                    where('isActive', '==', true),
                    where('startDate', '<=', now.toISOString()),
                    where('endDate', '>=', now.toISOString()),
                    orderBy('startDate', 'asc')
                )
            );

            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.()?.toISOString(),
                updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString()
            })) as UpdateWindow[];
        } catch (error) {
            console.error('Erro ao buscar janelas de atualização ativas:', error);
            throw new Error('Falha ao carregar janelas de atualização ativas');
        }
    }

    // Verificar se existe janela ativa no momento
    static async hasActiveWindow(): Promise<boolean> {
        try {
            const activeWindows = await this.getActiveWindows();
            return activeWindows.length > 0;
        } catch (error) {
            console.error('Erro ao verificar janela ativa:', error);
            return false;
        }
    }

    // Criar nova janela de atualização
    static async create(data: CreateUpdateWindowData): Promise<string> {
        try {
            const now = Timestamp.now();
            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...data,
                createdAt: now,
                updatedAt: now
            });

            return docRef.id;
        } catch (error) {
            console.error('Erro ao criar janela de atualização:', error);
            throw new Error('Falha ao criar janela de atualização');
        }
    }

    // Atualizar janela de atualização
    static async update(id: string, data: Partial<EditUpdateWindowData>): Promise<void> {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, {
                ...data,
                updatedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Erro ao atualizar janela de atualização:', error);
            throw new Error('Falha ao atualizar janela de atualização');
        }
    }

    // Deletar janela de atualização
    static async delete(id: string): Promise<void> {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await deleteDoc(docRef);
        } catch (error) {
            console.error('Erro ao deletar janela de atualização:', error);
            throw new Error('Falha ao deletar janela de atualização');
        }
    }

    // Verificar se colaborador pode atualizar perfil
    static async canUpdateProfile(collaboratorId?: string): Promise<{
        canUpdate: boolean;
        activeWindow?: UpdateWindow;
        reason?: string;
    }> {
        try {
            const activeWindows = await this.getActiveWindows();

            if (activeWindows.length === 0) {
                return {
                    canUpdate: false,
                    reason: 'Não há janela de atualização ativa no momento'
                };
            }

            // Por enquanto, usar a primeira janela ativa
            // No futuro, pode implementar lógica mais complexa baseada no colaborador
            const activeWindow = activeWindows[0];

            return {
                canUpdate: true,
                activeWindow
            };
        } catch (error) {
            console.error('Erro ao verificar permissão de atualização:', error);
            return {
                canUpdate: false,
                reason: 'Erro ao verificar permissões'
            };
        }
    }
}